import { Component, inject, NgZone, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { UserService } from '../../../services/user.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { ImageProcessingService } from '../../../services/image-processing.service';
import { ProcessedFile } from '../../../helpers/file.helper';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatPrefix } from '@angular/material/form-field';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrls: ['./upload-files.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [ImageProcessingService],
  imports: [MatIcon, MatProgressBar, MatPrefix, MatDivider, MatButton, MatDialogClose, MatDialogContent]
})
export class UploadFilesComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<UploadFilesComponent>);
  #escapeDialogHelper = inject(EscapeDialogHelper);
  #imageProcessingService = inject(ImageProcessingService);
  #messagingService = inject(MessagingService);
  #ngZone = inject(NgZone);
  #userService = inject(UserService);

  data = inject(MAT_DIALOG_DATA);

  #allowDocumentType = ['pdf', 'xlsx', 'txt', 'docx', 'png', 'jpeg', 'jpg'];
  #allowImageType = ['png', 'jpeg', 'jpg'];
  #maxSize = 10; // Max size in MB

  wrongFormatMessage = signal('');
  maxSizeExceedMessage = signal('');
  uploadedFiles = signal<number>(0);
  totalFiles = signal<number>(0);
  progress = signal<number>(0);
  images: ProcessedFile[] = [];
  documents: File[] | ProcessedFile[] = [];

  ngOnInit(): void {
    if (this.data.isQuote || this.data.isInvoice) {
      this.totalFiles.set(1);
      this.validateQuote();
    } else {
      this.totalFiles.set(this.data.files.length);
      this.validateFiles();
    }

    this.#escapeDialogHelper.escapeDialog(this.#dialogRef);
  }

  async validateFiles(): Promise<void> {
    const filesWrongFormat = [];
    const filesExceedMaxSize = [];

    for (let i = 0; i < this.totalFiles(); i++) {
      const filename = this.data.files[i].name;
      const fileSize = this.data.files[i].size / 1024 / 1024; // In MB
      const extension = filename.split('.').pop().toLowerCase();

      if (
        (!this.data.isPhotos && !this.#allowDocumentType.includes(extension)) ||
        (this.data.isPhotos && !this.#allowImageType.includes(extension))
      ) {
        filesWrongFormat.push(filename);
      } else if (fileSize > this.#maxSize) {
        filesExceedMaxSize.push(filename);
      }
    }

    if (filesWrongFormat.length > 0 || filesExceedMaxSize.length > 0) {
      if (filesWrongFormat.length > 0) {
        this.wrongFormatMessage.set(
          filesWrongFormat.join(', ') +
            (filesWrongFormat.length > 1 ? ' are not valid files. ' : ' is not a valid file. ') +
            `${
              this.data.isPhotos
                ? 'The image format must be jpg, jpeg or png'
                : 'The document format must be png, jpeg, jpg, pdf, xlsx, txt or docx'
            }.`
        );
      }
      if (filesExceedMaxSize.length > 0) {
        this.maxSizeExceedMessage.set(
          filesExceedMaxSize.join(', ') +
            (filesExceedMaxSize.length > 1 ? ' are ' : ' is ') +
            'over 10mb and could not be uploaded.'
        );
      }
    } else {
      if (this.data.isPhotos) {
        this.images = await this.#imageProcessingService.getScaleDownImages(this.data.files);
      } else {
        const imagesFiles = [...this.data.files].filter(({ name }) =>
          this.#allowImageType.includes(name.substring(name.lastIndexOf('.') + 1).toLowerCase())
        );
        const images = await this.#imageProcessingService.getScaleDownImages(imagesFiles);
        const documentsFiles = [...this.data.files].filter(
          ({ name }) => !this.#allowImageType.includes(name.substring(name.lastIndexOf('.') + 1).toLowerCase())
        );
        this.documents.push(...documentsFiles, ...images);
      }
      this.uploadFiles();
    }
  }

  uploadFiles(): void {
    if (this.data.files.length > this.uploadedFiles()) {
      const formData: FormData = new FormData();
      if (this.data.isPhotos) {
        const { name, blob } = this.images[this.uploadedFiles()];
        formData.append('filename', name);
        formData.append('image', blob);
      } else {
        const documentFile = this.documents[this.uploadedFiles()] as any;
        const fileName = documentFile.name;
        formData.append('filename', fileName);
        formData.append('document', documentFile.blob ?? documentFile, fileName);
      }
      this.uploadJobFiles(formData, this.data.isPhotos);
    } else {
      this.#dialogRef.close('Success');
    }
  }

  uploadJobFiles(formData: FormData, isPhotoToUpload: boolean): void {
    this.uploadedFiles.set(this.uploadedFiles() + 1);
    this.#userService
      .uploadJobFile(this.data.jobId, formData, isPhotoToUpload ? 'uploadJobPhoto' : 'uploadJobDocument')
      .subscribe({
        next: () => {
          this.#ngZone.run(() => {
            this.progress.set((this.uploadedFiles() / this.totalFiles()) * 100);
            if (this.data.files.length === this.uploadedFiles()) {
              this.#dialogRef.close('Success');
            } else {
              this.uploadFiles();
            }
          });
        },
        error: (error) => {
          if (error.error === 'VIRUS_FOUND') {
            // ignore
            this.uploadFiles();
          } else {
            this.#messagingService.error(`Could not upload ${isPhotoToUpload ? 'photos' : 'documents'}.`);
            this.stopUpload();
          }
        }
      });
  }

  validateQuote(): void {
    const filename = this.data.files.length ? this.data.files[0].name : this.data.files.name;
    const imageSize = (this.data.files.length ? this.data.files[0].size : this.data.files.size) / 1024 / 1024; // In MB
    const extension = filename.split('.').pop().toLowerCase();
    if (extension !== 'xml') {
      this.wrongFormatMessage.set(filename + ' is not a valid file. The quote format must be xml.');
    } else if (imageSize > this.#maxSize) {
      this.maxSizeExceedMessage.set(filename + ' is over 10mb and could not be uploaded.');
    } else {
      this.uploadQuoteOrInvoice();
    }
  }

  uploadQuoteOrInvoice(): void {
    const formData: FormData = new FormData();
    formData.append('quote', this.data.files.length ? this.data.files[0] : this.data.files);
    this.uploadedFiles.set(1);
    this.progress.set(100);
    if (this.data.isQuote) {
      this.#userService.importQuote(this.data.jobId, formData, this.data.importVehicleInfo).subscribe(
        () => {
          this.#dialogRef.close('Success');
        },
        (error) => {
          if (error.error === 'VIRUS_FOUND') {
            this.stopUpload();
          } else if (error.error === 'DUPLICATE_QUOTE_NUMBER') {
            this.wrongFormatMessage.set('A quote with this quote number is already associated with the job.');
          } else if (error.error === 'INVALID_REQUEST') {
            this.#messagingService.error('Could not upload quote: cannot parse XML');
            this.stopUpload();
          } else {
            this.#messagingService.error('Could not upload quote.');
            this.stopUpload();
          }
        }
      );
    } else {
      this.#userService.importInvoice(this.data.jobId, this.data.invoiceNumber, formData).subscribe(
        () => {
          this.#dialogRef.close('Success');
        },
        (error) => {
          if (error.error === 'VIRUS_FOUND') {
            this.stopUpload();
          } else if (error.error === 'DUPLICATE_INVOICE_NUMBER') {
            this.wrongFormatMessage.set('An invoice with this invoice number is already associated with the job.');
          } else if (error.error === 'NUMBER_OF_QUOTES_IN_CLAIM_NOT_MATCH_NUMBER_OF_QUOTES_DOCUMENT_IMPORTED') {
            this.wrongFormatMessage.set(
              'The number of quote(s) in the claim does not match the number of quote(s) in the file being imported.'
            );
          } else if (error.error === 'QUOTE_NUMBER_NOT_FOUND') {
            this.wrongFormatMessage.set('A quote in the file being imported does not match any quote in the claim.');
          } else {
            this.#messagingService.error('Could not upload invoice.');
            this.stopUpload();
          }
        }
      );
    }
  }

  stopUpload(): void {
    this.data.files = [];
    this.#dialogRef.close();
  }
}
